import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { ALPHANUMERIC_VALIDATION_TEXT, SPECIAL_CHARS } from '../../constants/Common';
import { xml2json } from '../../form-designer/utils/runtime.utils';
import {
  CONTAINERS_AS_CONTROLS,
  CONTAINERS_WITH_WITH_CHILDREN,
  CONTROL_TYPES_WITH_DEFAULT_DATA_MEMBER,
  CONTROLS_WITH_SPECIAL_MOVEMENT,
  CROSS_TAB_CHILDREN,
  DEFAULT_CONTROLS_PROPERTIES,
  FIELDS_MAPPING_FOR_NEW_CONTROL,
  LEGACY_CONTAINER_IDS,
  MOVE_BUTTON_TYPES,
  REPORT_CONTROL_TYPE,
  REPORT_LAYOUT_ID,
  ROOT_CONTROL_ID,
} from '../constants/report-designer.constants';
import {
  CLOSE_GROUP_VALUES,
  CONDITION_VALUES,
  FILTER_VALUES,
  OPEN_GROUP_VALUES,
  OPERATOR_VALUES,
  SORT_DIRECTION_VALUES,
  VALIDATION_VALUES,
  YES_NO_CONDITIONAL_VALUES,
} from '../constants/report-designer.properties.constants';
import { REPORT_PUBLISH_VIRTUAL_ENTITY_ERROR_MESSAGES } from '../constants/validation.constants';

export const getIsMoveBtnDisabled = (id, type, parentChildrenControlTypes, moveType) => {
  const currentIndex = parentChildrenControlTypes.findIndex(({ controlId }) => controlId === id);

  if (CONTROLS_WITH_SPECIAL_MOVEMENT.includes(type)) {
    const hasSameTypeSiblingOnTop = parentChildrenControlTypes
      .slice(0, currentIndex)
      .find(({ controlType }) => controlType === type);
    const hasSameTypeSiblingBottom = parentChildrenControlTypes
      .slice(currentIndex + 1)
      .find(({ controlType }) => controlType === type);

    return (
      (moveType === MOVE_BUTTON_TYPES.UP && !hasSameTypeSiblingOnTop) ||
      (moveType === MOVE_BUTTON_TYPES.DOWN && !hasSameTypeSiblingBottom)
    );
  }

  return (
    (moveType === MOVE_BUTTON_TYPES.UP && currentIndex === 0) ||
    (moveType === MOVE_BUTTON_TYPES.DOWN && currentIndex === parentChildrenControlTypes.length - 1)
  );
};

export const getIsDeleteControlDisabled = (type, id, parentChildrenControlTypes) => {
  if (
    [
      ...CROSS_TAB_CHILDREN,
      REPORT_CONTROL_TYPE.CHART_GROUP,
      REPORT_CONTROL_TYPE.CHART_VALUE,
    ].includes(type)
  ) {
    const hasSiblingOfSameType = !!parentChildrenControlTypes.find(
      ({ controlType, controlId }) => controlType === type && id !== controlId
    );

    return !hasSiblingOfSameType;
  }

  return false;
};

export const getNewParentChildrenNodesForMovedControl = (
  parentChildrenControlTypes,
  id,
  moveType,
  controlType
) => {
  const currentIndex = parentChildrenControlTypes.findIndex(({ controlId }) => controlId === id);
  const childrenNodes = parentChildrenControlTypes.map(({ controlId }) => controlId);

  childrenNodes.splice(currentIndex, 1);

  if (CONTROLS_WITH_SPECIAL_MOVEMENT.includes(controlType)) {
    if (moveType === MOVE_BUTTON_TYPES.UP) {
      const topSibling = parentChildrenControlTypes.slice(0, currentIndex);

      const indexOfFirstTopSiblingOfSameType = topSibling
        .reverse()
        .findIndex(({ controlType: type }) => type === controlType);

      childrenNodes.splice(topSibling.length - indexOfFirstTopSiblingOfSameType - 1, 0, id);
      return childrenNodes;
    }

    if (moveType === MOVE_BUTTON_TYPES.DOWN) {
      const indexOfFirstBottomSiblingOfSameType = parentChildrenControlTypes
        .slice(currentIndex + 1)
        .findIndex(({ controlType: type }) => type === controlType);

      childrenNodes.splice(currentIndex + indexOfFirstBottomSiblingOfSameType + 1, 0, id);
      return childrenNodes;
    }
  }

  if (moveType === MOVE_BUTTON_TYPES.UP) {
    childrenNodes.splice(currentIndex - 1, 0, id);
  } else if (moveType === MOVE_BUTTON_TYPES.DOWN) {
    childrenNodes.splice(currentIndex + 1, 0, id);
  }

  return childrenNodes;
};

export const isExpressionValue = (text) => {
  if (typeof text !== 'string') {
    return false;
  }
  return _.startsWith(text, 'eval||');
};

export const createEntityTreeNode = (entityNode, reportModelEntityDefinition, searchText) => {
  const { Id, Name, Alias, TranslatedName } = entityNode;
  const fullNodeName = `${Alias}.${Name}`;

  const entityFields = reportModelEntityDefinition?.[fullNodeName]?.fields || [];
  const childrenNodes = entityFields
    .sort((a, b) =>
      a.translatedName.localeCompare(b.translatedName, undefined, { sensitivity: 'base' })
    )
    .map(({ fullName, translatedName }) => ({
      nodeId: fullName,
      label: `${translatedName} (${fullName})`,
      value: `{|${translatedName}|${fullName}|}`,
      translatedValue: translatedName,
      parentNodeId: fullNodeName,
    }));

  const entityFieldNode = {
    nodeId: `${fullNodeName}-fields`,
    label: 'Fields',
    childNodes: searchText
      ? childrenNodes.filter((field) =>
          field.label.toLowerCase().includes(searchText.trim().toLowerCase())
        )
      : childrenNodes,
  };

  return {
    nodeId: Id,
    label: `${TranslatedName} (${fullNodeName})`,
    childNodes: [entityFieldNode],
    value: fullNodeName,
  };
};

export const getFilteredDataWithFields = (node) => {
  if (!node.childNodes?.length) {
    return node;
  }

  const filteredChildNodes = node.childNodes
    .map(getFilteredDataWithFields)
    .filter((childNode) => !childNode.childNodes || childNode.childNodes.length);

  return {
    ...node,
    childNodes: filteredChildNodes.length ? filteredChildNodes : [],
  };
};

export const renderNodeTree = (
  reportModelEntityHierarchy,
  reportModelEntityDefinition,
  searchText = ''
) => {
  if (!reportModelEntityHierarchy || !reportModelEntityDefinition) {
    return [];
  }

  const renderTree = (parentId = '', currentNodeStructure) => {
    //root element
    if (parentId === '') {
      const rootEntityNode = reportModelEntityHierarchy.find(
        ({ ParentId }) => ParentId === parentId
      );

      const entityTreeNode = createEntityTreeNode(
        rootEntityNode,
        reportModelEntityDefinition,
        searchText
      );

      if (rootEntityNode.HasChildren) {
        return renderTree(rootEntityNode.Id, entityTreeNode);
      }

      return entityTreeNode;
    }

    const childEntities = reportModelEntityHierarchy.filter(
      ({ ParentId }) => ParentId === parentId
    );

    childEntities.forEach((childEntityNode) => {
      const entityTreeNode = createEntityTreeNode(
        childEntityNode,
        reportModelEntityDefinition,
        searchText
      );

      currentNodeStructure.childNodes = [...currentNodeStructure.childNodes, entityTreeNode];

      if (childEntityNode.HasChildren) {
        return renderTree(childEntityNode.Id, entityTreeNode);
      }
    });

    return currentNodeStructure;
  };

  const data = renderTree();

  return searchText ? getFilteredDataWithFields(data) : data;
};

export const _find = (collection, key, value) => {
  for (const o of collection) {
    for (const [k, v] of Object.entries(o)) {
      if (k === key && v === value) {
        return o;
      }
      if (Array.isArray(v)) {
        const _o = _find(v, key, value);
        if (_o) {
          return _o;
        }
      }
    }
  }
};

export const setDefaultControlProperties = (control, defaultDataMember) => {
  if (CONTROL_TYPES_WITH_DEFAULT_DATA_MEMBER.includes(control.controlType)) {
    control.dataMember = control.dataMember || defaultDataMember;
  } else if (control.controlType === REPORT_CONTROL_TYPE.GROUP_TREND) {
    control.groupDataMember = control.groupDataMember || defaultDataMember;
  }

  const defaultProperties = DEFAULT_CONTROLS_PROPERTIES[control.controlType];

  if (!defaultProperties) {
    return;
  }

  for (const prop in defaultProperties) {
    if (prop in control) {
      continue;
    }

    control[prop] = defaultProperties[prop];
  }
};

export const getPropertyValueToDisplay = (propValue) =>
  String(propValue).toLowerCase() === 'true'
    ? true
    : String(propValue).toLowerCase() === 'false'
    ? false
    : _.isString(propValue)
    ? propValue.replace(/eval\|\|/g, '')
    : propValue;

export const createNewControl = ({
  controlType,
  parentId,
  mappingValues = {},
  defaultDataMember,
}) => {
  const mappingFields = FIELDS_MAPPING_FOR_NEW_CONTROL[controlType] || [];

  const newControl = {
    controlId: uuidv4(),
    controlType,
    parentId,
    childrenNodes: [],
  };

  mappingFields.forEach(({ propName, source, expression = '' }) => {
    const mappingValue = mappingValues[source] || '';

    const newValue = expression
      ? 'eval||' + expression.replace('VALUE_TO_REPLACE', mappingValue)
      : mappingValue;

    newControl[propName] = newValue;
  });

  setDefaultControlProperties(newControl, defaultDataMember);

  return newControl;
};

export const createDefaultChildrenControls = ({
  parentControl,
  parentId,
  defaultControls,
  defaultDataMember,
}) =>
  defaultControls.map((controlType) => {
    const newControlId = uuidv4();

    parentControl.childrenNodes.push(newControlId);

    const newControl = {
      controlId: newControlId,
      controlType,
      parentId,
      childrenNodes: [],
    };

    setDefaultControlProperties(newControl, defaultDataMember);

    return newControl;
  });

export const getDropDownConditionValuesByAccessor = (accessor) => {
  const accessorsMap = {
    openGroup: OPEN_GROUP_VALUES,
    translateLabel: YES_NO_CONDITIONAL_VALUES,
    condition: CONDITION_VALUES,
    value: FILTER_VALUES,
    validateValue: VALIDATION_VALUES,
    closeGroup: CLOSE_GROUP_VALUES,
    operator: OPERATOR_VALUES,
    sort: SORT_DIRECTION_VALUES,
  };

  return accessorsMap[accessor] ?? [];
};

export const getChildControlType = (parentControlType) => {
  const typesMap = {
    [REPORT_CONTROL_TYPE.FILTER_CONDITION_CONTAINER]: REPORT_CONTROL_TYPE.FILTER_CONDITION,
    [REPORT_CONTROL_TYPE.DATA_FILTER_CONDITION_CONTAINER]:
      REPORT_CONTROL_TYPE.DATA_FILTER_CONDITION,
    [REPORT_CONTROL_TYPE.SORT_CONDITION_CONTAINER]: REPORT_CONTROL_TYPE.SORT_CONDITION,
  };

  return typesMap[parentControlType];
};

export const translateEntityField = (translations = {}, valueToTranslate = '') =>
  translations[valueToTranslate] || valueToTranslate;

export const getSimpleBlockTitle = ({
  controlType = '',
  dataField = '',
  valueLabel = '',
  category = '',
  columnGroupDisplay = '',
  label = '',
  rowGroupDisplay = '',
  translations,
}) => {
  switch (controlType) {
    case REPORT_CONTROL_TYPE.GROUP_TREND:
      return isExpressionValue(label)
        ? 'Expression'
        : translateEntityField(translations, label) || 'Trend';

    case REPORT_CONTROL_TYPE.DATA_COLUMN:
      return isExpressionValue(label)
        ? 'Expression'
        : translateEntityField(translations, label) || 'Data Column';

    case REPORT_CONTROL_TYPE.CHART_VALUE:
      return !valueLabel
        ? 'Chart Value'
        : isExpressionValue(valueLabel)
        ? 'Expression'
        : translateEntityField(translations, valueLabel);

    case REPORT_CONTROL_TYPE.CHART_GROUP:
      return isExpressionValue(category)
        ? 'Expression'
        : translateEntityField(translations, category) || 'Chart Group';

    case REPORT_CONTROL_TYPE.CROSS_TAB_COLUMN:
      return !columnGroupDisplay
        ? 'Cross Tab Column'
        : isExpressionValue(columnGroupDisplay)
        ? 'Expression'
        : translateEntityField(translations, columnGroupDisplay);

    case REPORT_CONTROL_TYPE.CROSS_TAB_DATA:
      return !label && !dataField
        ? 'Cross Tab Data'
        : isExpressionValue(label)
        ? 'Expression'
        : label
        ? translateEntityField(translations, label)
        : isExpressionValue(dataField)
        ? 'Expression'
        : translateEntityField(translations, dataField);

    case REPORT_CONTROL_TYPE.CROSS_TAB_ROW:
      return isExpressionValue(rowGroupDisplay)
        ? 'Expression'
        : translateEntityField(translations, rowGroupDisplay) || 'Cross Tab Row';

    default:
      return 'Value';
  }
};

export const convertContainerChildrenToPlainObject = (rootContainerId, reportModel) => {
  let convertedChildren = {};

  const rootContainerChildren = reportModel[rootContainerId];
  const rootContainerChildrenIds = Object.keys(rootContainerChildren);

  const rootContainerControl = {
    [rootContainerId]: {
      controlId: rootContainerId,
      controlType: rootContainerId,
      parentId: ROOT_CONTROL_ID,
      childrenNodes: rootContainerChildrenIds,
    },
  };

  rootContainerChildrenIds.forEach((containerChildId) => {
    if (CONTAINERS_WITH_WITH_CHILDREN.includes(containerChildId)) {
      const allChildren = rootContainerChildren[containerChildId];

      const directContainerChildrenNodes = Object.values(allChildren).reduce(
        (acc, { parentId, controlId }) => {
          if (parentId === containerChildId) {
            return [...acc, controlId];
          }

          return acc;
        },
        []
      );

      convertedChildren = {
        ...rootContainerControl,
        ...convertedChildren,
        [containerChildId]: {
          controlId: containerChildId,
          controlType: containerChildId,
          parentId: rootContainerId,
          childrenNodes: directContainerChildrenNodes,
        },
        ...allChildren,
      };

      return;
    }

    const childControlProps = rootContainerChildren[containerChildId];

    convertedChildren = {
      ...convertedChildren,
      [containerChildId]: {
        controlId: containerChildId,
        controlType: containerChildId,
        parentId: rootContainerId,
        ...childControlProps,
      },
    };
  });

  return convertedChildren;
};

export const convertReportModelToPlainObject = (reportModel) => {
  const rootContainersIds = Object.keys(reportModel);

  let plainReportModel = {
    formBox: {
      controlId: ROOT_CONTROL_ID,
      parentId: 'root',
      childrenNodes: rootContainersIds,
    },
  };

  rootContainersIds.forEach((rootContainerId) => {
    const isContainerWithChildren = CONTAINERS_WITH_WITH_CHILDREN.includes(rootContainerId);

    if (rootContainerId === REPORT_CONTROL_TYPE.DISTRIBUTION_TAB) {
      reportModel[rootContainerId]?.distributionList?.forEach((item) => (item.rowId = item.id));
    }

    const rootContainer = isContainerWithChildren
      ? convertContainerChildrenToPlainObject(rootContainerId, reportModel)
      : {
          [rootContainerId]: {
            controlId: rootContainerId,
            controlType: rootContainerId,
            parentId: ROOT_CONTROL_ID,
            ...reportModel[rootContainerId],
          },
        };

    plainReportModel = {
      ...plainReportModel,
      ...rootContainer,
    };
  });

  return plainReportModel;
};

export const removeCustomControlProps = (control = {}) => {
  const { controlId } = control;
  let propsToRemove = [];

  if (CONTAINERS_AS_CONTROLS.includes(controlId)) {
    propsToRemove = ['controlId', 'parentId'];
  }

  if (REPORT_CONTROL_TYPE.DISTRIBUTION === controlId) {
    propsToRemove = ['controlId', 'parentId', 'controlType'];
  }

  if (REPORT_LAYOUT_ID === controlId) {
    propsToRemove = ['parentId'];
  }

  return propsToRemove.length ? _.omit(control, propsToRemove) : control;
};

export const convertChildrenToInitFormat = (reportModel, containerId) => {
  const childrenNodes = reportModel[containerId].childrenNodes;

  return childrenNodes.reduce((acc, childId) => {
    const childrenNodes = reportModel[childId]?.childrenNodes || [];

    const nestedChildren = childrenNodes.reduce(
      (acc, id) => ({ ...acc, [id]: removeCustomControlProps(reportModel[id]) }),
      {}
    );

    const allChildren = {
      [childId]: removeCustomControlProps(reportModel[childId]),
      ...nestedChildren,
    };

    return { ...acc, ...allChildren };
  }, {});
};

export const convertContainerChildrenToInitFormat = (mainContainerId, reportModel) => {
  let convertedChildrenControls = {};

  const mainContainerChildrenIds = reportModel[mainContainerId].childrenNodes;

  mainContainerChildrenIds.forEach((childContainerId) => {
    const isContainerWithChildren = CONTAINERS_WITH_WITH_CHILDREN.includes(childContainerId);
    const legacyContainerId =
      Object.keys(LEGACY_CONTAINER_IDS).find(
        (legacyContainerId) => LEGACY_CONTAINER_IDS[legacyContainerId] === childContainerId
      ) || childContainerId;

    convertedChildrenControls = {
      ...convertedChildrenControls,
      [legacyContainerId]: isContainerWithChildren
        ? convertChildrenToInitFormat(reportModel, childContainerId)
        : removeCustomControlProps(reportModel[childContainerId]),
    };
  });

  return convertedChildrenControls;
};

export const getReportModelForSave = (reportModel, reportInfo, isSaveAs, reportName) => {
  let reportModelForSave = {};

  const rootContainerChildren = reportModel[ROOT_CONTROL_ID].childrenNodes;

  rootContainerChildren.forEach((rootContainerId) => {
    const isContainerWithChildren = CONTAINERS_WITH_WITH_CHILDREN.includes(rootContainerId);

    reportModelForSave = {
      ...reportModelForSave,
      [rootContainerId]: isContainerWithChildren
        ? convertContainerChildrenToInitFormat(rootContainerId, reportModel)
        : removeCustomControlProps(reportModel[rootContainerId]),
    };
  });

  if (isSaveAs) {
    reportInfo.ReportKey = reportName.trim();
    reportInfo.ReportId = null;
    reportInfo.virtualEntityName = null;
  }

  return {
    createEntityView: false,
    publish: false,
    overwrite: isSaveAs,
    report: { reportInfo, reportModel: reportModelForSave },
  };
};

export const getPayloadForCreateVirtualEntity = ({
  reportModel,
  reportInfo,
  virtualEntityName,
}) => {
  let reportModelForSave = {};

  const rootContainerChildren = reportModel[ROOT_CONTROL_ID].childrenNodes;

  rootContainerChildren.forEach((rootContainerId) => {
    const isContainerWithChildren = CONTAINERS_WITH_WITH_CHILDREN.includes(rootContainerId);

    reportModelForSave = {
      ...reportModelForSave,
      [rootContainerId]: isContainerWithChildren
        ? convertContainerChildrenToInitFormat(rootContainerId, reportModel)
        : removeCustomControlProps(reportModel[rootContainerId]),
    };
  });

  reportInfo.isVirtualEntityNameExist = !!reportInfo.virtualEntityName;
  reportInfo.virtualEntityName = virtualEntityName.trim();

  return {
    createEntityView: true,
    publish: false,
    overwrite: false,
    report: { reportInfo, reportModel: reportModelForSave },
  };
};

export const replaceLegacyContainerIds = (reportModel) => {
  const rootContainersId = Object.keys(reportModel);

  return rootContainersId.reduce((acc, rootContainerId) => {
    const childContainersId = Object.keys(reportModel[rootContainerId]);

    childContainersId.forEach((childContainerId) => {
      const newControlId = LEGACY_CONTAINER_IDS[childContainerId] || childContainerId;
      const prevControls = acc[rootContainerId] || {};

      acc = {
        ...acc,
        [rootContainerId]: {
          ...prevControls,
          [newControlId]: reportModel[rootContainerId][childContainerId],
        },
      };
    });

    return acc;
  }, {});
};

export const preprocessReportModel = (reportModel, defaultDataMember) => {
  const preprocessedReportModel = replaceLegacyContainerIds(reportModel);
  const plainReportModel = convertReportModelToPlainObject(preprocessedReportModel);

  Object.values(plainReportModel).forEach((control) => {
    setDefaultControlProperties(control, defaultDataMember);
  });

  return plainReportModel;
};

export const convertEntityFieldsTranslation = (entityFields = []) =>
  Object.keys(entityFields).reduce((acc, entityKey) => {
    entityFields[entityKey]?.fields?.forEach(
      ({ fullName, translatedName }) =>
        (acc = {
          ...acc,
          [fullName]: translatedName,
        })
    );

    return acc;
  }, {});

export const validateVirtualEntiyNameOnBlur = (virtualEntityName = '') => {
  if (!virtualEntityName.length) {
    return {
      hasError: true,
      errorText: REPORT_PUBLISH_VIRTUAL_ENTITY_ERROR_MESSAGES.NAME_REQUIRED,
    };
  }

  if (SPECIAL_CHARS.test(virtualEntityName)) {
    return {
      hasError: true,
      errorText: ALPHANUMERIC_VALIDATION_TEXT,
    };
  }

  if (virtualEntityName.length > 25) {
    return { hasError: true, errorText: REPORT_PUBLISH_VIRTUAL_ENTITY_ERROR_MESSAGES.MAX_LENGTH };
  }

  return { hasError: false, errorText: '' };
};

export const validateReportXml = ({ xml, errorMessage }) => {
  const parsedJson = xml2json(xml);
  const { reportDesign, ssReportInfo } = parsedJson?.reportPackage ?? {};

  if (!reportDesign || !ssReportInfo) {
    throw new Error(errorMessage);
  }
};
